import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  ViewChildren,
  QueryList,
  OnDestroy,
  signal,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { ImportExportComponent } from '../settings/dialogs/import-export/import-export.component';
import { filter, Subscription } from 'rxjs';
import { translateAddressError, exportJsonToExcel, readExcelFile } from '../globals';
import { MatSnackBar } from '@angular/material/snack-bar';
import { addDoc, collection, doc, setDoc } from 'firebase/firestore';
import { db } from '../app.component';
import { WarningDialog } from '../global-dialogs/warning/warning.component';
import { AddressValidationService } from '../services/address-validation.service';
import { FailedAddress } from '../interfaces';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton, MatFabButton } from '@angular/material/button';
import { MatTabGroup, MatTab, MatTabLabel } from '@angular/material/tabs';
import { CharacteristicsComponent } from './tabs/characteristics/characteristics.component';
import { StandardLocationsComponent } from './tabs/standard-locations/standard-locations.component';
import { PhoneNumbersComponent } from './tabs/phone-numbers/phone-numbers.component';
import { ParticularitiesComponent } from './tabs/particularities/particularities/particularities.component';
import { MatProgressBar } from '@angular/material/progress-bar';

@Component({
  selector: 'app-ride-preparation',
  templateUrl: './ride-preparation.component.html',
  styleUrl: './ride-preparation.component.scss',
  imports: [
    MatIcon,
    MatIconButton,
    MatTabGroup,
    MatTab,
    MatTabLabel,
    CharacteristicsComponent,
    StandardLocationsComponent,
    PhoneNumbersComponent,
    ParticularitiesComponent,
    MatFabButton,
    MatProgressBar,
  ],
})
export class RidePreparationComponent implements OnInit, OnDestroy {
  @ViewChild('uploader', { static: false }) uploader!: ElementRef<HTMLInputElement>;
  @ViewChildren('tab') tabComponents!: QueryList<unknown>;
  selectedIndex = signal(0);
  processing = signal(false);
  fileUploaded!: File;

  tabs = [
    {
      key: 'standardLocations',
      name: 'Standaardlocaties',
      url: 'standard-locations',
      icon: 'location_on',
    },
    {
      key: 'characteristics',
      name: 'Kenmerken',
      url: 'characteristics',
      icon: 'more',
    },
    {
      key: 'particularities',
      name: 'Bijzonderheden',
      url: 'particularities',
      icon: 'settings_accessibility',
    },
    {
      key: 'phoneNumbers',
      name: 'Telefoonnummers',
      url: 'phone-numbers',
      icon: 'call',
    },
  ];

  private routeSubscription: Subscription = new Subscription();

  get activeTab(): any {
    return this.tabComponents?.toArray()[this.selectedIndex()];
  }

  get selectedTabKey() {
    return this.tabs[this.selectedIndex()]?.key;
  }

  constructor(
    public dialog: MatDialog,
    public router: Router,
    public snackBar: MatSnackBar,
    public addressValidationService: AddressValidationService,
  ) {}

  ngOnInit(): void {
    // Subscribe to router navigation events to handle view changes
    this.routeSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.updateSelectedIndex();
      });
    // Set initial view
    this.updateSelectedIndex();
  }

  ngOnDestroy(): void {
    this.routeSubscription.unsubscribe();
  }

  updateUrl() {
    const tab = this.tabs[this.selectedIndex()];
    if (!tab) {
      return;
    }
    this.router.navigate([`/ride-preparation/${tab.url}`]);
  }

  updateSelectedIndex() {
    const activeUrl = this.getActiveTabUrlFromRoute();
    const index = this.tabs.findIndex((tab) => tab.url === activeUrl);
    if (index >= 0) {
      this.selectedIndex.set(index);
      return;
    }
    this.selectedIndex.set(0);
  }

  private getActiveTabUrlFromRoute(): string | null {
    return (
      this.tabs.find((tab) => this.router.url.includes(`/ride-preparation/${tab.url}`))?.url ?? null
    );
  }

  openImportExport() {
    if (this.processing()) {
      return;
    }
    const dialogRef = this.dialog.open(ImportExportComponent, {
      width: '350px',
    });
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result.type === 'Export') {
        await this.exportData();
      }
      if (result.type === 'Import') {
        this.uploader.nativeElement.click();
      }
    });
  }

  async exportData() {
    this.processing.set(true);
    const tabName = this.tabs[this.selectedIndex()]?.name;
    if (!tabName) {
      this.processing.set(false);
      return;
    }
    const exportData = this.activeTab.getExportData();
    const rowsArray: Record<string, any>[] = [];
    exportData.items.forEach((item: Record<string, any>) => {
      const rowObj: Record<string, any> = {};
      exportData.columns.forEach((column: { name: string; itemProperty: string }) => {
        let value: any;
        if (column.itemProperty.includes('.')) {
          // Used to properly get the value of nested properties of an item, e.g. calendarStyle.color
          value = item;
          column.itemProperty.split('.').forEach((nestedProperty: string) => {
            value = value[nestedProperty];
          });
        } else if (typeof item[column.itemProperty] === 'boolean') {
          value = item[column.itemProperty] ? 'WAAR' : 'ONWAAR';
        } else {
          value = item[column.itemProperty];
        }
        rowObj[column.name] = value ?? '';
      });
      rowsArray.push(rowObj);
    });

    if (rowsArray.length > 0) {
      if (exportData.options.sortBy) {
        const sortBy = exportData.options.sortBy;
        rowsArray.sort(function (a, b) {
          if (a[sortBy].toLowerCase() < b[sortBy].toLowerCase()) {
            return -1;
          }
          if (a[sortBy].toLowerCase() > b[sortBy].toLowerCase()) {
            return 1;
          }
          return 0;
        });
      }

      await exportJsonToExcel(rowsArray, tabName, tabName);
    } else {
      this.snackBar.open(`Er zijn geen ${tabName.toLowerCase()} om te exporteren`, 'X', {
        duration: 5000,
      });
    }
    this.processing.set(false);
  }

  async uploadedFile(event: Event) {
    const input = event.target as HTMLInputElement | null;
    if (!input?.files?.length) {
      return;
    } else {
      this.fileUploaded = input.files[0];
      await this.readExcel();
    }
  }

  async readExcel() {
    this.processing.set(true);
    const importConfig = this.activeTab.getImportConfig();

    try {
      if (!importConfig.uniqueKey) {
        throw new Error('No unique key defined for import');
      }

      const spreadsheet = await readExcelFile(this.fileUploaded);
      if (!spreadsheet || Object.keys(spreadsheet).length === 0) {
        throw new Error('Spreadsheet is empty or could not be read');
      }

      const columnMapping = this.mapColumns(spreadsheet[1], importConfig);
      const result = this.processRows(spreadsheet, columnMapping, importConfig);
      await this.importData(
        result.importedData,
        importConfig,
        result.duplicateRows,
        result.duplicateRowsData,
      );
    } catch (error) {
      console.error('Import error:', error);
      this.snackBar.open('Er is iets misgegaan tijdens het verwerken van het bestand', 'X', {
        duration: 5000,
      });
    } finally {
      this.processing.set(false);
      this.uploader.nativeElement.value = '';
    }
  }

  private mapColumns(headerRow: Record<string, any>, importConfig: any): Record<string, string> {
    const columnMapping: Record<string, string> = {};
    Object.keys(headerRow).forEach((key: string) => {
      const cellValue = headerRow[key];
      if (!cellValue) return;
      const val = cellValue.toString().toLowerCase().trim();
      columnMapping[key] = importConfig.columnSettings.find(
        (col: { columnName: string; itemProperty: string }) => col.columnName === val,
      )?.itemProperty;
    });
    return columnMapping;
  }

  private processRows(
    spreadsheet: Record<string, Record<string, any>>,
    columnMapping: Record<string, string>,
    importConfig: any,
  ): { importedData: any[]; duplicateRows: boolean; duplicateRowsData: any[] } {
    delete spreadsheet[1]; // Remove header row
    const importedData: any[] = [];
    const validRows: any[] = [];
    const requiredColumns = importConfig.columnSettings.filter(
      (col: { required?: boolean }) => col.required,
    );
    let duplicateRows = false;

    for (const rowKey of Object.keys(spreadsheet)) {
      const rowObj = this.buildRowObject(spreadsheet[rowKey], columnMapping, importConfig);
      if (!this.isValidRow(rowObj, requiredColumns)) {
        duplicateRows = true;
      } else if (importConfig.tab === 'standardLocations') {
        validRows.push(rowObj);
      } else if (this.isUniqueRow(rowObj, importedData, importConfig)) {
        importedData.push(rowObj);
      } else {
        duplicateRows = true;
      }
    }

    if (importConfig.tab === 'standardLocations') {
      const rowsByUniqueKey = new Map<any, any[]>();
      validRows.forEach((row) => {
        const key = row[importConfig.uniqueKey];
        const rows = rowsByUniqueKey.get(key) ?? [];
        rows.push(row);
        rowsByUniqueKey.set(key, rows);
      });
      const duplicateRowsData = validRows.filter(
        (row) => (rowsByUniqueKey.get(row[importConfig.uniqueKey])?.length ?? 0) > 1,
      );
      return {
        importedData: validRows.filter(
          (row) => (rowsByUniqueKey.get(row[importConfig.uniqueKey])?.length ?? 0) === 1,
        ),
        duplicateRows: duplicateRows || duplicateRowsData.length > 0,
        duplicateRowsData,
      };
    }

    return { importedData, duplicateRows, duplicateRowsData: [] };
  }

  private buildRowObject(
    rowData: Record<string, any>,
    columnMapping: Record<string, string>,
    importConfig: any,
  ): Record<string, any> {
    const rowObj: Record<string, any> = {};
    for (const colKey of Object.keys(rowData)) {
      const colName = columnMapping[colKey];
      if (colName) {
        const colSetting = importConfig.columnSettings.find(
          (colSetting: { itemProperty: string; defaultValue?: any; type?: string; min?: number }) =>
            colSetting.itemProperty === colName,
        );
        let value =
          rowData[colKey] && rowData[colKey] !== ''
            ? rowData[colKey]
            : (colSetting?.defaultValue ?? null);
        value = this.convertValue(value, colSetting);

        if (colName.includes('.')) {
          this.setNestedProperty(rowObj, colName, value);
        } else {
          rowObj[colName] = value;
        }
      }
    }
    return rowObj;
  }

  private setNestedProperty(obj: Record<string, any>, path: string, value: any): void {
    const keys = path.split('.');
    let current = obj;
    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]]) {
        current[keys[i]] = {};
      }
      current = current[keys[i]];
    }
    current[keys[keys.length - 1]] = value;
  }

  private convertValue(value: any, colSetting?: any): any {
    if (!colSetting?.type) return value;
    let convertedValue;
    switch (colSetting.type) {
      case 'boolean':
        convertedValue = value?.toString().toLowerCase() === 'waar' || value === true;
        break;
      case 'number':
        convertedValue = isNaN(Number(value)) ? (colSetting?.defaultValue ?? null) : Number(value);
        if (
          colSetting.min !== undefined &&
          convertedValue !== null &&
          convertedValue < colSetting.min
        ) {
          convertedValue = colSetting.min;
        }
        break;
      default:
        convertedValue = value;
    }
    return convertedValue;
  }

  private isValidRow(
    rowObj: Record<string, any>,
    requiredColumns: { itemProperty: string }[],
  ): boolean {
    for (const reqColumn of requiredColumns) {
      const val = reqColumn.itemProperty
        .split('.')
        .reduce((current: any, key: string) => current?.[key], rowObj);
      if (val === null || val === '' || val === undefined) {
        return false;
      }
    }
    return true;
  }

  isUniqueRow(
    rowObj: Record<string, any>,
    importedData: Record<string, any>[],
    importConfig: { uniqueKey: string; tab?: string },
  ): boolean {
    if (importConfig.tab === 'standardLocations') {
      return !importedData.some(
        (item: Record<string, any>) =>
          item[importConfig.uniqueKey] === rowObj[importConfig.uniqueKey],
      );
    }

    return !importedData.some(
      (item: Record<string, any>) => JSON.stringify(item) === JSON.stringify(rowObj),
    );
  }

  async importData(
    importedData: any[],
    importConfig: any,
    duplicateRows: boolean = false,
    duplicateRowsData: Record<string, any>[] = [],
  ) {
    const collectionPaths = {
      characteristics: `organisations/${importConfig.organisationId}/characteristics`,
      standardLocations: `organisations/${importConfig.organisationId}/addresses`,
      phoneNumbers: `organisations/${importConfig.organisationId}/numbers`,
    };
    const collectionPath = collectionPaths[importConfig.tab as keyof typeof collectionPaths];

    try {
      if (importedData.length === 0 && duplicateRowsData.length === 0) {
        throw new Error('No valid data available for import');
      }

      // Special handling for standard locations: validate addresses before saving
      let validatedData = importedData;
      let failedAddresses: FailedAddress[] = [];
      let failedImportRows: Record<string, any>[] = [];
      let failedRowsDownloadSucceeded = true;

      if (importConfig.tab === 'standardLocations') {
        failedAddresses = duplicateRowsData.map((row) => ({
          name: row.name || 'Onbekend',
          address: row.address || '',
          error: `Dubbele waarde voor ${importConfig.uniqueKey} in importbestand`,
        }));
        const normalizedAddress = (address: string): string =>
          this.addressValidationService.normalizeAddressForComparison(address);
        const reusableItems = new Set<Record<string, any>>();
        const itemsToValidate = importedData.filter((item: Record<string, any>) => {
          const existingData = importConfig.existingData.find(
            (existingItem: Record<string, any>) =>
              existingItem[importConfig.uniqueKey] === item[importConfig.uniqueKey],
          );
          const importedAddress = normalizedAddress(item.address);
          const existingAddress = existingData ? normalizedAddress(existingData.address) : '';
          const canReuseCoordinates = Boolean(
            existingData?.coordinates &&
            existingData.coordinatesFor &&
            importedAddress === existingAddress &&
            importedAddress === existingData.coordinatesFor,
          );

          if (canReuseCoordinates) {
            item.coordinates = { ...existingData.coordinates };
            item.coordinatesFor = importedAddress;
            reusableItems.add(item);
            return false;
          }

          return true;
        });

        // Validate only new or changed locations; unchanged locations reuse stored ORS data.
        const validationResult = itemsToValidate.length
          ? await this.addressValidationService.validateAddressesBulk(
              itemsToValidate.map((item: Record<string, any>) => item.address),
            )
          : {
              validAddresses: [],
              invalidAddresses: [],
              totalProcessed: 0,
              validCount: 0,
              invalidCount: 0,
            };

        const validAddressResults = new Map(
          validationResult.validAddresses.map((validAddress) => [
            validAddress.originalAddress,
            validAddress,
          ]),
        );
        validatedData = importedData
          .filter((item) => reusableItems.has(item) || validAddressResults.has(item.address))
          .map((item) => {
            if (reusableItems.has(item)) {
              return item;
            }
            const validation = validAddressResults.get(item.address);
            return {
              ...item,
              ...(validation?.coordinates
                ? {
                    coordinates: validation.coordinates,
                    coordinatesFor: this.addressValidationService.normalizeAddressForComparison(
                      item.address,
                    ),
                  }
                : {}),
            };
          });

        // Match duplicate invalid addresses to distinct input rows in order.
        const itemsByAddress = new Map<string, Record<string, any>[]>();
        itemsToValidate.forEach((item) => {
          const items = itemsByAddress.get(item.address) ?? [];
          items.push(item);
          itemsByAddress.set(item.address, items);
        });
        failedAddresses = [
          ...failedAddresses,
          ...validationResult.invalidAddresses.map((invalidAddr) => {
            const matchingItems = itemsByAddress.get(invalidAddr.originalAddress) ?? [];
            const item = matchingItems.shift();
            const error = invalidAddr.error
              ? translateAddressError(invalidAddr.error)
              : invalidAddr.requiresConfirmation
                ? 'Adres niet geïmporteerd: ORS kon het adres niet volledig verifiëren'
                : 'Adres niet gevonden via ORS';
            return {
              name: item?.name || 'Onbekend',
              address: invalidAddr.originalAddress,
              error,
            };
          }),
        ];
        failedImportRows = failedAddresses.map((failedAddress) => ({
          Naam: failedAddress.name,
          Adres: failedAddress.address,
          'Reden van fout bij importeren': failedAddress.error,
        }));

        if (failedImportRows.length > 0) {
          try {
            await exportJsonToExcel(
              failedImportRows,
              'Standaardlocaties-import-fouten',
              'Standaardlocaties',
            );
          } catch (error) {
            failedRowsDownloadSucceeded = false;
            console.error('Failed to export standard-location import errors:', error);
          }
        }

        // If all addresses failed validation, show error and return
        if (validatedData.length === 0) {
          this.showFailedImportRows(failedAddresses, false, failedRowsDownloadSucceeded);
          return;
        }
      }

      const batchSize = 50;
      let promises = [];
      let rowsSkipped = false;

      for (const item of validatedData as Record<string, any>[]) {
        if (!item[importConfig.uniqueKey]) {
          rowsSkipped = true;
          continue;
        }
        const existingData = importConfig.existingData.find(
          (existingItem: Record<string, any>) =>
            existingItem[importConfig.uniqueKey] === item[importConfig.uniqueKey],
        );
        if (existingData) {
          promises.push(
            setDoc(doc(db, `${collectionPath}/${existingData.id}`), item, { merge: true }),
          );
        } else {
          promises.push(addDoc(collection(db, collectionPath), item));
        }
        if (promises.length === batchSize) {
          await Promise.allSettled(promises);
          promises = [];
        }
      }
      if (promises.length > 0) {
        await Promise.allSettled(promises);
      }

      // Show import result, including failed addresses if any
      if (importConfig.tab === 'standardLocations' && failedAddresses.length > 0) {
        this.showFailedImportRows(
          failedAddresses,
          validatedData.length > 0,
          failedRowsDownloadSucceeded,
        );
      } else {
        this.showImportResult(rowsSkipped || duplicateRows, importConfig);
      }
    } catch (error) {
      console.error('Import error:', error);
      this.snackBar.open('Er ging iets mis bij het verwerken van het bestand.', 'X', {
        duration: 5000,
      });
    }
  }

  private showImportResult(hasSkipped: boolean, importConfig: any) {
    if (hasSkipped) {
      this.dialog.open(WarningDialog, {
        width: '600px',
        data: {
          dialogType: 'importPartialSuccess',
        },
      });
    } else {
      const tabName = this.tabs.find((tab) => tab.key === importConfig.tab)?.name.toLowerCase();
      this.snackBar.open(`De ${tabName ?? 'gegevens'} zijn geïmporteerd.`, 'X', {
        duration: 5000,
      });
    }
  }

  private showFailedImportRows(
    failedAddresses: FailedAddress[],
    hasSuccessfulImports: boolean = false,
    downloadSucceeded: boolean = true,
  ) {
    this.dialog.open(WarningDialog, {
      width: '700px',
      data: {
        dialogType: 'importFailedRows',
        optionalData: {
          failedRows: failedAddresses,
          hasSuccessfulImports,
          downloadSucceeded,
        },
      },
    });
  }
}
